import { IkeFieldType } from '@ikegps/core-api';
import { FormModelField } from '../../form.field';
import { FormModelFieldSelectList } from '../../form.select.list';
import { IPFSpanType, SpanType } from '../../../pole/integrations';
import { FormModelFieldSubForm } from '../form.subform';
import { getOtherAssignedSubformFieldIds } from '../form.subform.field.util';

export const SPAN_ENVIRONMENT_VALUES = [
	'STREET',
	'HIGHWAY',
	'PEDESTRIAN',
	'PARALLEL_TO_STREET',
	'UNLIKELY_PARALLEL_TO_STREET',
	'DRIVEWAY / VEHICLE ACCESS',
	'ALLEY',
	'RAILROAD',
	'RURAL',
	'FARM',
	'WATER_WITH SAILBOATS',
	'WATER_WITHOUT_SAILBOATS',
] as const;

const spanTypeOptions = {
	foreSpan: [
		IPFSpanType.FORE_SPAN, // IPF
		SpanType.NextPole, // DDS, OCalc, PoleForeman Legacy, Spida
	],
	backSpan: [
		IPFSpanType.BACK_SPAN, // IPF
		SpanType.PreviousPole, // DDS, OCalc, PoleForeman Legacy, Spida
	],
	otherSpan: [
		IPFSpanType.OTHER, // IPF
		SpanType.Other, // DDS, OCalc, PoleForeman Legacy, Spida
	],
	buildingSpan: [
		SpanType.Building, // DDS, OCalc, PoleForeman Legacy, Spida
	],
} as const;

export function isValidSpanVectorField(field: FormModelField): boolean {
	const excludeId = field.parent?.stateValue.spanTypeState?.vectorFieldId;
	const otherIds = getOtherAssignedSubformFieldIds(field, excludeId);
	return field.type === IkeFieldType.VECTOR && !otherIds.includes(field.id);
}

export function isValidSpanTypeField(field: FormModelField): boolean {
	const excludeId = field.parent?.stateValue.spanTypeState?.typeFieldId;
	const otherIds = getOtherAssignedSubformFieldIds(field, excludeId);
	if (!FormModelFieldSelectList.isSelectList(field)) {
		return false;
	}
	// Must include a valid Fore Span option.
	if (!field.options.some((option) => spanTypeOptions.foreSpan.find((valid) => valid === option.value))) {
		return false;
	}
	// Must include a valid Back Span option.
	if (!field.options.some((option) => spanTypeOptions.backSpan.find((valid) => valid === option.value))) {
		return false;
	}
	// Must include a valid Other Span option.
	if (!field.options.some((option) => spanTypeOptions.otherSpan.find((valid) => valid === option.value))) {
		return false;
	}
	return !otherIds.includes(field.id);
}

export function isValidSpanEnvironmentField(field: FormModelField): boolean {
	const excludeId = field.parent?.stateValue.spanTypeState?.environmentFieldId;
	const otherIds = getOtherAssignedSubformFieldIds(field, excludeId);
	return !!FormModelFieldSelectList.isSelectList(field) && !otherIds.includes(field.id);
}

export function isValidDefaultEnvironmentField(field: FormModelField): boolean {
	if (!isValidSpanEnvironmentField(field) || !FormModelFieldSelectList.isSelectList(field)) {
		return false;
	}
	const fieldValues = field.options.slice(1).map((option) => option.value);
	return (
		fieldValues.length === SPAN_ENVIRONMENT_VALUES.length &&
		SPAN_ENVIRONMENT_VALUES.every((v) => fieldValues.includes(v))
	);
}

/**
 * Validates that a field can be used as a mid span IKE photo field.
 * Must be a TRUE_SIZE field type which stores CaptureFileJSONObject[].
 */
export function isValidMidSpanIkePhotoField(field: FormModelField): boolean {
	const excludeId = field.parent?.stateValue.spanTypeState?.midSpanIkePhotoFieldId;
	const otherIds = getOtherAssignedSubformFieldIds(field, excludeId);
	return field.type === IkeFieldType.TRUE_SIZE && !otherIds.includes(field.id);
}

/**
 * Check if a span subform has complete type state for two-way linking.
 * Returns true if all required fields (vector, type, midSpanPhoto) are configured.
 *
 * @param schema The subform field to check
 * @returns true if the span has complete configuration for two-way linking
 */
export function hasCompleteSpanTypeState(schema: FormModelFieldSubForm): boolean {
	const state = schema.subFormSpanTypeState;
	if (!state) return false;
	// environment field id is optional
	return !!(state.vectorFieldId && state.typeFieldId && state.midSpanIkePhotoFieldId);
}
